import { tKey } from 'ember-intl';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';

export const INTERCOM_HELP_LINK =
  'en/articles/11703342-new-budget-forecast-module';

export const DATE_GRANULARITY = {
  DAY: DATE_AGGREGATION.DAY,
  MONTH: DATE_AGGREGATION.MONTH,
};

export const DEFAULT_ENTRY_TITLE_TRANSLATIONS = {
  budget: tKey('settings.budget.title'),
  forecast: tKey('settings.forecast.title'),
};

export const COLUMN_PROPERTIES = {
  NAME: 'name',
  TOTAL: 'total',
  ROOMS: 'rooms',
  ADR: 'adr',
  REVENUE: 'revenue',
} as const;

export const DATA_TYPES = {
  BUDGET: 'budget',
  FORECAST: 'forecast',
  OTB: 'otb',
  BI_FORECAST: 'bi_forecast',
  FINAL_LY: 'final_ly',
  FINANCE_FORECAST: 'finance_forecast',
  RMS_FORECAST: 'rms_forecast',
  GROUP_BLOCK: 'group_block',
  CAPACITY: 'capacity',
} as const;
export type DataType = (typeof DATA_TYPES)[keyof typeof DATA_TYPES];

/**
 * Default DataType order that is used in the budget view mode.
 */
export const BUDGET_VIEW_DATA_TYPE_ORDER: DataType[] = [
  DATA_TYPES.OTB,
  DATA_TYPES.GROUP_BLOCK,
  DATA_TYPES.FINAL_LY,
  DATA_TYPES.FORECAST,
  DATA_TYPES.BI_FORECAST,
  DATA_TYPES.FINANCE_FORECAST,
  DATA_TYPES.RMS_FORECAST,
];

/**
 * Default DataType order that is used in the budget edit mode.
 */
export const BUDGET_EDIT_DATA_TYPE_ORDER: DataType[] = [
  ...BUDGET_VIEW_DATA_TYPE_ORDER,
  DATA_TYPES.CAPACITY,
];

/**
 * Default DataType order that is used in the forecast view mode.
 */
export const FORECAST_VIEW_DATA_TYPE_ORDER: DataType[] = [
  DATA_TYPES.OTB,
  DATA_TYPES.GROUP_BLOCK,
  DATA_TYPES.FINAL_LY,
  DATA_TYPES.BUDGET,
  DATA_TYPES.BI_FORECAST,
  DATA_TYPES.FINANCE_FORECAST,
  DATA_TYPES.RMS_FORECAST,
];

/**
 * Default DataType order that is used in the forecast edit mode.
 */
export const FORECAST_EDIT_DATA_TYPE_ORDER: DataType[] = [
  ...FORECAST_VIEW_DATA_TYPE_ORDER,
  DATA_TYPES.CAPACITY,
];

export const ADDITIONAL_DATA_TYPES: DataType[] = [DATA_TYPES.CAPACITY];

export const DATA_TYPE_TRANSLATIONS: Record<DataType, string> = {
  [DATA_TYPES.BUDGET]: tKey('settings.budget.title'),
  [DATA_TYPES.FORECAST]: tKey('settings.budget_forecast.user_forecast'),
  [DATA_TYPES.OTB]: tKey('settings.budget_forecast.otb'),
  [DATA_TYPES.BI_FORECAST]: tKey('settings.budget_forecast.bi_forecast'),
  [DATA_TYPES.FINAL_LY]: tKey('settings.budget_forecast.ly_finals'),
  [DATA_TYPES.CAPACITY]: tKey('settings.budget_forecast.inventory_excl_ooo'),
  [DATA_TYPES.FINANCE_FORECAST]: tKey('budget_forecast.finance_forecast'),
  [DATA_TYPES.RMS_FORECAST]: tKey('budget_forecast.rms_forecast'),
  [DATA_TYPES.GROUP_BLOCK]: tKey('budget_forecast.group_block'),
};

export const SEGMENT_LEVELS = {
  TOTALS: 'totals',
  GROUPS: 'groups',
  CATEGORIES: 'categories',
  SUB_CATEGORIES: 'sub_categories',
} as const;
export type SegmentLevel = (typeof SEGMENT_LEVELS)[keyof typeof SEGMENT_LEVELS];

export type DateGranularity =
  (typeof DATE_GRANULARITY)[keyof typeof DATE_GRANULARITY];

export const DISTRIBUTION_METHODS = {
  EQUAL: 'equal',
  LAST_YEAR_FINALS: 'final_ly',
} as const;
export type DistributionMethod =
  (typeof DISTRIBUTION_METHODS)[keyof typeof DISTRIBUTION_METHODS];

export const DEFAULT_DISTRIBUTION_METHOD: DistributionMethod =
  DISTRIBUTION_METHODS.LAST_YEAR_FINALS;

export const DISTRIBUTION_METHOD_TRANSLATIONS: Record<
  DistributionMethod,
  string
> = {
  [DISTRIBUTION_METHODS.EQUAL]: tKey(
    'settings.budget_forecast.distribute.equally',
  ),
  [DISTRIBUTION_METHODS.LAST_YEAR_FINALS]: tKey(
    'settings.budget_forecast.distribute.ly_finals',
  ),
};

export const DATE_GRANULARITY_TRANSLATIONS: Record<DateGranularity, string> = {
  [DATE_GRANULARITY.DAY]: tKey('general.day'),
  [DATE_GRANULARITY.MONTH]: tKey('general.month'),
};

export function isValidDistributionMethod(
  method: string,
): method is DistributionMethod {
  return Object.values(DISTRIBUTION_METHODS).includes(
    method as DistributionMethod,
  );
}
